import {
  GraphQLFieldConfig,
  GraphQLNonNull,
  GraphQLInputObjectType,
  GraphQLID,
  GraphQLString,
  GraphQLEnumType,
  GraphQLList
} from 'graphql'
import {
  GenericMutationResponseType,
  GenericMutationResponse
} from '../types/generic-mutation-response'
import { Context } from '../shared'
import {
  LibraryFilterArg,
  libraryFilterArgType
} from '../types/library-filter-arg'
import { mapLibraryFilters } from '../endpoint-helpers'

export type ApplicationLibraryStatus =
  | 'NONE'
  | 'REPORTED'
  | 'REMEDIATED'
  | 'NOT_A_PROBLEM'

export const applicationLibraryStatusType = new GraphQLEnumType({
  name: 'ApplicationLibraryStatusType',
  values: {
    REPORTED: { value: 'REPORTED' },
    REMEDIATED: { value: 'REMEDIATED' },
    NOT_A_PROBLEM: { value: 'NOT_A_PROBLEM' }
  }
})

export interface UpdateApplicationLibraryStatusMutationArgs {
  readonly input: {
    readonly organizationID: string
    readonly applicationID: string
    readonly hashes: string[]
    readonly status: ApplicationLibraryStatus
  }
}

const updateApplicationLibraryStatusArgs = {
  input: {
    type: new GraphQLInputObjectType({
      name: 'UpdateApplicationLibraryStatusMutationArg',
      description:
        'Mutation input type for updating application library statuses',
      fields: () => ({
        organizationID: {
          description:
            'The ID of the organization that owns the application libraries being mutated',
          type: new GraphQLNonNull(GraphQLID)
        },
        applicationID: {
          description: 'The ID of the application that the libraries belong to',
          type: new GraphQLNonNull(GraphQLID)
        },
        hashes: {
          description: 'A list of hashes of the libraries being updated',
          type: new GraphQLList(GraphQLString)
        },
        status: {
          description: 'The new status of the application libraries',
          type: new GraphQLNonNull(applicationLibraryStatusType)
        }
      })
    })
  }
}

export const updateApplicationLibraryStatus: GraphQLFieldConfig<
  any,
  Context,
  UpdateApplicationLibraryStatusMutationArgs
> = {
  description: 'Update application library statuses',
  type: new GraphQLNonNull(GenericMutationResponseType),
  args: updateApplicationLibraryStatusArgs,
  resolve: async (_source, args, context): Promise<GenericMutationResponse> => {
    if (!args.input) {
      throw new Error('mutation input parameter is required')
    }

    const { organizationID, applicationID, hashes, status } = args.input

    await context.connector.put(
      `${organizationID}/libraries/${applicationID}/status`,
      {
        status,
        hashes
      }
    )

    return {
      _placeholder: true
    }
  }
}

export interface UpdateApplicationLibraryStatusByFiltersMutationArgs {
  readonly input?: {
    readonly organizationID: string
    readonly applicationID: string
    readonly filters: LibraryFilterArg
    readonly excludedHashes: string[]
    readonly status: ApplicationLibraryStatus
  }
}

const updateApplicationLibraryStatusByFiltersArgs = {
  input: {
    type: new GraphQLInputObjectType({
      name: 'UpdateApplicationLibraryStatusByFiltersMutationArg',
      description:
        'Mutation input type for updating application library statuses by filters.',
      fields: () => ({
        organizationID: {
          description:
            'The ID of the organization that owns the application libraries being mutated',
          type: new GraphQLNonNull(GraphQLID)
        },
        applicationID: {
          description: 'The ID of the application that the libraries belong to',
          type: new GraphQLNonNull(GraphQLID)
        },
        filters: {
          type: new GraphQLNonNull(libraryFilterArgType)
        },
        excludedHashes: {
          description: 'A list of libraries to exclude from the filter',
          type: new GraphQLNonNull(
            new GraphQLList(new GraphQLNonNull(GraphQLID))
          )
        },
        status: {
          description: 'The new status of the application libraries',
          type: new GraphQLNonNull(applicationLibraryStatusType)
        }
      })
    })
  }
}

export const updateApplicationLibraryStatusByFilters: GraphQLFieldConfig<
  any,
  Context,
  UpdateApplicationLibraryStatusByFiltersMutationArgs
> = {
  description: 'Update application library status with filters',
  type: new GraphQLNonNull(GenericMutationResponseType),
  args: updateApplicationLibraryStatusByFiltersArgs,
  resolve: async (_source, args, context): Promise<GenericMutationResponse> => {
    if (!args.input) {
      throw new Error('mutation input parameter is required')
    }

    const { organizationID, applicationID, filters, excludedHashes, status } =
      args.input

    await context.connector.put(
      `${organizationID}/libraries/bulk/${applicationID}/status`,
      {
        filters: {
          ...mapLibraryFilters(filters),
          excludedHashes
        },
        status
      }
    )

    return {
      _placeholder: true
    }
  }
}
